import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Trophy, RotateCcw, Lock } from "lucide-react";
import { toast } from "sonner";

export default function Quiz() {
  const [params, setParams] = useSearchParams();
  const country = params.get("country") || "mixed";
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [locked, setLocked] = useState(false);

  const TABS = [
    { key: "mixed", label: t("quiz.tabMixed") },
    { key: "FR", label: "🇫🇷 France" },
    { key: "CH", label: "🇨🇭 Suisse" },
    { key: "IT", label: "🇮🇹 Italie" },
  ];

  const load = (c) => {
    setResult(null); setAnswers({}); setLocked(false);
    api.get(`/quiz?country=${c}`)
      .then(({ data }) => setQuestions(data))
      .catch((err) => {
        if (err.response?.status === 402 || err.response?.status === 401) setLocked(true);
      });
  };

  useEffect(() => { load(country); /* eslint-disable-next-line */ }, [country]);

  const submit = async () => {
    if (Object.keys(answers).length < questions.length) { toast.error(t("quiz.answerAll")); return; }
    try {
      const { data } = await api.post("/quiz/submit", { answers, country });
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch { toast.error(t("quiz.validateError")); }
  };

  if (locked) {
    return (
      <div className="max-w-2xl mx-auto px-5 py-24 text-center paper-noise min-h-[70vh]">
        <Lock className="w-12 h-12 text-[#E06D53] mx-auto mb-6" />
        <h1 className="font-serif text-4xl tracking-tight text-[#2C2A28] mb-3">{t("quiz.lockedTitle")}</h1>
        <p className="text-[#5C5854] mb-8">{t("quiz.lockedDesc")}</p>
        <Button onClick={() => navigate("/tarifs")} className="rounded-full bg-[#E06D53] hover:bg-[#C95B42] text-white h-12 px-8" data-testid="quiz-paywall-cta">{t("quiz.lockedCta")}</Button>
      </div>
    );
  }

  const pct = result ? Math.round((result.score / result.total) * 100) : 0;

  return (
    <div className="max-w-3xl mx-auto px-5 py-12 paper-noise min-h-[70vh]">
      <span className="text-xs tracking-[0.25em] uppercase font-semibold text-[#E06D53]">{t("quiz.badge")}</span>
      <h1 className="font-serif text-4xl md:text-5xl tracking-tighter text-[#2C2A28] mt-3 mb-8">{t("quiz.title")}</h1>

      <div className="flex flex-wrap gap-2 mb-10">
        {TABS.map((tab) => (
          <button key={tab.key} onClick={() => setParams({ country: tab.key })} data-testid={`quiz-tab-${tab.key}`}
            className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${country === tab.key ? "bg-[#1D4E89] text-white" : "bg-white border border-[#E5E0D8] text-[#5C5854] hover:border-[#E06D53]"}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {result && (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl border border-[#E5E0D8] p-8 mb-8 text-center" data-testid="quiz-result">
          <Trophy className="w-10 h-10 text-[#E9C46A] mx-auto mb-3" />
          <div className="font-serif text-5xl text-[#1D4E89]">{result.score}/{result.total}</div>
          <p className="text-[#5C5854] mt-2">{pct}{t("quiz.correctSuffix")} · {pct >= 70 ? t("quiz.scoreGood") : t("quiz.scoreKeep")}</p>
          <Button onClick={() => load(country)} variant="outline" className="rounded-full mt-5" data-testid="quiz-retry">
            <RotateCcw className="w-4 h-4 mr-2" /> {t("quiz.retry")}
          </Button>
        </motion.div>
      )}

      <div className="space-y-5">
        {questions.map((q, qi) => {
          const detail = result?.details.find((d) => d.id === q.id);
          return (
            <div key={q.id} className="bg-white rounded-2xl border border-[#E5E0D8] p-6" data-testid={`quiz-question-${qi}`}>
              <div className="flex items-start gap-3 mb-4">
                <span className="w-7 h-7 rounded-full bg-[#FBEDE9] text-[#C95B42] text-sm font-semibold flex items-center justify-center shrink-0">{qi + 1}</span>
                <h3 className="text-lg font-medium text-[#2C2A28]">{q.question}</h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {q.options.map((opt, oi) => {
                  const selected = answers[q.id] === oi;
                  let cls = "border-[#E5E0D8] hover:border-[#E06D53]";
                  if (result && detail) {
                    if (oi === detail.correct) cls = "border-[#2D6A4F] bg-[#EAF4EF]";
                    else if (selected && !detail.is_correct) cls = "border-[#D62828] bg-[#FBE9E9]";
                    else cls = "border-[#E5E0D8] opacity-70";
                  } else if (selected) cls = "border-[#E06D53] bg-[#FBEDE9]";
                  return (
                    <button key={oi} disabled={!!result} onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                      className={`text-left rounded-xl border-2 px-4 py-3 text-sm transition-colors ${cls}`} data-testid={`quiz-q${qi}-opt${oi}`}>
                      <span className="flex items-center gap-2">
                        {result && detail && oi === detail.correct && <CheckCircle2 className="w-4 h-4 text-[#2D6A4F] shrink-0" />}
                        {result && detail && selected && !detail.is_correct && <XCircle className="w-4 h-4 text-[#D62828] shrink-0" />}
                        {opt}
                      </span>
                    </button>
                  );
                })}
              </div>
              {result && detail && (
                <p className="text-sm text-[#5C5854] mt-4 bg-[#FAF9F6] rounded-xl p-3 border border-[#E5E0D8]" data-testid={`quiz-explanation-${qi}`}>
                  <strong className="text-[#2C2A28]">{t("quiz.explanation")}</strong>{detail.explanation}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {!result && questions.length > 0 && (
        <Button onClick={submit} className="w-full rounded-full h-12 bg-[#E06D53] hover:bg-[#C95B42] text-white mt-8 text-base" data-testid="quiz-submit">
          {t("quiz.submit")}
        </Button>
      )}
    </div>
  );
}
